import { createApi } from '@reduxjs/toolkit/query/react';
import { noopBaseQuery } from '@src/providers/AppStateProvider/noopBaseQuery';
import type { BaseWsQuery } from '@src/providers/AppStateProvider/streamingDataSlice';
import { buildWsEndpointBuilder } from '@src/providers/AppStateProvider/wsEndpointBuilder';
import { subDays } from 'date-fns';
import { baseRestApi, sendToastOnQueryFulfilled } from '../../../utils/rtkQuery';
import type { AppNotification } from '../AppNotificationsMonitorContainer';
import { processNotificationsForSound } from './processNotificationSounds';

export interface NotificationArchiveResponse {
  type: 'NotificationArchiveResponse';
  ts: string;
  data: Array<{
    SuccessfulNotificationIDs: string[];
    FailedNotificationIDs: string[];
  }>;
}

const appNotificationsApiInternal = baseRestApi.injectEndpoints({
  endpoints: builder => ({
    // Individual endpoints
    getExistingNotifications: builder.query<AppNotification[], void>({
      query: () => ({
        method: 'GET',
        url: `/notifications`,
      }),
      // exclude archived or withdrawn notifications
      transformResponse: (response: AppNotification[]) =>
        response.filter(notification => notification.State !== 'Archived' && notification.State !== 'Withdrawn'),
    }),
    getNotificationById: builder.query<AppNotification | undefined, { notificationId: string }>({
      query: ({ notificationId }) => ({
        method: 'GET',
        url: `/notifications/${notificationId}`,
      }),
      transformResponse: (response: AppNotification[]) => response[0],
    }),
    archiveNotifications: builder.mutation({
      query: (data: { notificationId: string }) => ({
        url: `/notifications/${data.notificationId}/archive`,
        method: 'POST',
        credentials: 'include',
      }),
    }),
    archiveBatchNotifications: builder.mutation<NotificationArchiveResponse, { NotificationIDs: string[] }>({
      query: (data: { NotificationIDs: string[] }) => ({
        url: `/notifications/archive`,
        method: 'POST',
        body: data,
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      }),
      onQueryStarted: sendToastOnQueryFulfilled({
        errorMessage: 'Unable to dismiss notifications',
      }),
      extraOptions: {
        maxRetries: 2,
      },
    }),
  }),
});

export const appNotificationsApi = {
  endpoints: appNotificationsApiInternal.endpoints,
  util: appNotificationsApiInternal.util,
};

export const appNotificationsStreamingApi = createApi({
  reducerPath: 'appNotificationsStreamingApi',
  baseQuery: noopBaseQuery,
  keepUnusedDataFor: 30,
  endpoints: builder => {
    const dateWrapper = {
      StartDate: subDays(new Date(), 30).toISOString(),
    };
    return {
      getStreamingNotifications: buildWsEndpointBuilder<typeof builder>(builder).query<
        Map<string, AppNotification>,
        BaseWsQuery
      >({
        buildRequest: ({ tag = 'streamingDataSlice', ...args }) => {
          return { name: 'Notification', tag, ...dateWrapper, ...args };
        },
        initializeCache: (data: AppNotification[]) => {
          // exclude archived or withdrawn notifications
          return data
            .filter(notification => notification.State !== 'Archived' && notification.State !== 'Withdrawn')
            .reduce((map, notification) => map.set(notification.NotificationID, notification), new Map());
        },
        updateCache: (draft, data: AppNotification[]) => {
          // Sounds should only be played for new notifications
          processNotificationsForSound(data);

          return new Map(data.reduce((map, notification) => map.set(notification.NotificationID, notification), draft));
        },
      }),
    };
  },
});

export const {
  useGetExistingNotificationsQuery,
  useArchiveNotificationsMutation,
  useArchiveBatchNotificationsMutation,
  useLazyGetNotificationByIdQuery,
} = appNotificationsApiInternal;
export const { useGetStreamingNotificationsQuery } = appNotificationsStreamingApi;
